import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Newspaper, ExternalLink, Calendar, Loader2, RefreshCw, Lock, Crown, Check } from 'lucide-react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { useUser } from '../context/UserContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NewsPage = () => {
  const { user, token, hasNewsAccess } = useUser();
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [subscribing, setSubscribing] = useState(false);

  const FREE_NEWS_LIMIT = 100; // All news are free
  const canAccessAllNews = true; // Everyone can access all news

  useEffect(() => {
    fetchNews();
  }, []);

  const fetchNews = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${API}/news`);
      setNews(response.data.articles || []);
      setLastUpdate(new Date().toLocaleTimeString('it-IT'));
    } catch (err) {
      console.error('Error fetching news:', err);
      setError('Impossibile caricare le notizie. Riprova più tardi.');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('it-IT', { 
        day: 'numeric', 
        month: 'long', 
        year: 'numeric' 
      });
    } catch {
      return dateString;
    }
  };

  const handleSubscribe = async () => {
    if (!user) {
      toast.error('Accedi per abbonarti');
      return;
    }
    
    setSubscribing(true);
    try {
      const response = await axios.post(`${API}/paypal/create-news-subscription`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.approval_url) {
        window.location.href = response.data.approval_url;
      }
    } catch (error) {
      console.error('Subscription error:', error);
      toast.error('Errore durante la creazione dell\'abbonamento. Riprova.');
    } finally {
      setSubscribing(false);
    }
  };

  const freeNews = news.slice(0, FREE_NEWS_LIMIT);
  const premiumNews = news.slice(FREE_NEWS_LIMIT);

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="news-page">
      {/* Hero Section */}
      <section className="relative py-16 md:py-24 overflow-hidden bg-black text-white">
        <div className="max-w-6xl mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full mb-6">
              <Newspaper className="h-4 w-4" />
              <span className="text-sm uppercase tracking-wider">QANTRI Magazine</span>
            </div>
            
            <h1 className="text-4xl md:text-6xl font-light mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              Tendenze & <span className="italic">Lifestyle</span>
            </h1>
            
            <p className="text-xl text-white/70 max-w-2xl mx-auto">
              Le ultime notizie dal mondo della moda, del design e del lifestyle.
            </p>
          </motion.div>
        </div>
      </section>

      {/* News Grid */}
      <section className="py-16 md:py-24 px-6 md:px-12">
        <div className="max-w-6xl mx-auto">
          {/* Update info */}
          {lastUpdate && !loading && (
            <div className="flex items-center justify-between mb-8">
              <p className="text-sm text-neutral-400">
                Ultimo aggiornamento: {lastUpdate}
              </p>
              <Button 
                onClick={fetchNews} 
                variant="ghost" 
                size="sm"
                className="text-neutral-500 hover:text-[#064E3B]"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Aggiorna
              </Button>
            </div>
          )}
          
          {loading ? (
            <div className="flex flex-col items-center justify-center py-20">
              <Loader2 className="h-10 w-10 text-[#064E3B] animate-spin mb-4" />
              <p className="text-neutral-500">Caricamento notizie...</p>
            </div>
          ) : error ? (
            <div className="text-center py-20">
              <p className="text-neutral-500 mb-4">{error}</p>
              <Button onClick={fetchNews} variant="outline" className="rounded-full">
                <RefreshCw className="h-4 w-4 mr-2" />
                Riprova
              </Button>
            </div>
          ) : news.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-neutral-500">Nessuna notizia disponibile al momento.</p>
            </div>
          ) : (
            <>
              {/* Free News Section */}
              <div className="mb-16">
                <h2 className="text-2xl font-light mb-8" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Notizie in <span className="italic">Evidenza</span>
                </h2>
                
                {/* Featured Article (First Free) */}
                {freeNews[0] && (
                  <motion.a
                    href={freeNews[0].link}
                    target="_blank"
                    rel="noopener noreferrer"
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="block mb-8 group"
                  >
                    <div className="relative rounded-3xl overflow-hidden bg-neutral-900 aspect-[21/9]">
                      {freeNews[0].image ? (
                        <img
                          src={freeNews[0].image}
                          alt={freeNews[0].title}
                          className="w-full h-full object-cover opacity-60 group-hover:opacity-70 group-hover:scale-105 transition-all duration-700"
                        />
                      ) : (
                        <div className="w-full h-full bg-gradient-to-br from-[#064E3B] to-[#0A6B4F]" />
                      )}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                      <div className="absolute bottom-0 left-0 right-0 p-8 md:p-12">
                        <span className="inline-block bg-[#D4AF37] text-black text-xs uppercase tracking-wider px-3 py-1 rounded-full mb-4">
                          Gratuito
                        </span>
                        <h2 className="text-2xl md:text-4xl text-white font-light mb-3 group-hover:text-[#D4AF37] transition-colors" style={{ fontFamily: 'Playfair Display, serif' }}>
                          {freeNews[0].title}
                        </h2>
                        {freeNews[0].description && (
                          <p className="text-white/70 text-sm md:text-base max-w-3xl line-clamp-2 mb-4">
                            {freeNews[0].description}
                          </p>
                        )}
                        <div className="flex items-center gap-4 text-white/50 text-sm">
                          {freeNews[0].source && <span>{freeNews[0].source}</span>}
                          {freeNews[0].date && (
                            <>
                              <span>•</span>
                              <span className="flex items-center gap-1">
                                <Calendar className="h-3 w-3" />
                                {formatDate(freeNews[0].date)}
                              </span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </motion.a>
                )}

                {/* Second Free Article */}
                {freeNews[1] && (
                  <motion.a
                    href={freeNews[1].link}
                    target="_blank"
                    rel="noopener noreferrer"
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                    className="block group"
                  >
                    <div className="bg-white rounded-2xl overflow-hidden border border-neutral-200 hover:shadow-xl transition-all duration-500 flex flex-col md:flex-row">
                      <div className="md:w-1/3 aspect-video md:aspect-auto overflow-hidden bg-neutral-100">
                        {freeNews[1].image ? (
                          <img
                            src={freeNews[1].image}
                            alt={freeNews[1].title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                          />
                        ) : (
                          <div className="w-full h-full bg-gradient-to-br from-neutral-200 to-neutral-300 flex items-center justify-center">
                            <Newspaper className="h-12 w-12 text-neutral-400" />
                          </div>
                        )}
                      </div>
                      <div className="flex-1 p-6">
                        <span className="inline-block bg-green-100 text-green-700 text-xs uppercase tracking-wider px-2 py-1 rounded-full mb-3">
                          Gratuito
                        </span>
                        <h3 className="text-xl font-medium mb-2 group-hover:text-[#064E3B] transition-colors">
                          {freeNews[1].title}
                        </h3>
                        {freeNews[1].description && (
                          <p className="text-neutral-500 text-sm line-clamp-2 mb-4">
                            {freeNews[1].description}
                          </p>
                        )}
                        <div className="flex items-center gap-4 text-xs text-neutral-400">
                          {freeNews[1].source && <span>{freeNews[1].source}</span>}
                          {freeNews[1].date && <span>{formatDate(freeNews[1].date)}</span>}
                        </div>
                      </div>
                    </div>
                  </motion.a>
                )}
              </div>

              {/* Premium News Section - Now Free */}
              {premiumNews.length > 0 && (
                <div>
                  <div className="flex items-center justify-between mb-8">
                    <h2 className="text-2xl font-light" style={{ fontFamily: 'Playfair Display, serif' }}>
                      Tutte le <span className="italic">Notizie</span>
                    </h2>
                    <div className="flex items-center gap-2 text-[#1a5c3a]">
                      <Check className="h-5 w-5" />
                      <span className="text-sm font-medium">Gratuito</span>
                    </div>
                  </div>

                  {/* All News Grid - Free for everyone */}
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {premiumNews.map((article, index) => (
                      <motion.a
                        key={index}
                        href={article.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: index * 0.05 }}
                        className="block group"
                      >
                        <div className="bg-white rounded-2xl overflow-hidden border border-neutral-200 hover:shadow-xl transition-all duration-500 h-full">
                          <div className="aspect-video overflow-hidden bg-neutral-100">
                            {article.image ? (
                              <img
                                src={article.image}
                                alt={article.title}
                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                              />
                            ) : (
                              <div className="w-full h-full bg-gradient-to-br from-[#064E3B] to-[#0A6B4F]" />
                            )}
                          </div>
                          <div className="p-5">
                            <h3 className="font-medium text-neutral-900 mb-2 line-clamp-2 group-hover:text-[#1a5c3a] transition-colors">
                              {article.title}
                            </h3>
                            <div className="flex items-center gap-2 text-neutral-500 text-xs">
                              {article.source && <span>{article.source}</span>}
                              {article.date && <span>{formatDate(article.date)}</span>}
                            </div>
                          </div>
                        </div>
                      </motion.a>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </section>
    </div>
  );
};

export default NewsPage;
